import { ThumbsUp, ThumbsDown, MessageSquare, Edit2, Trash2, Star, Clock, CheckCircle2, XCircle, AlertCircle, FileText } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";

// Approval status configurations
export const APPROVAL_STATUSES = [
  { value: "draft", label: "Draft", color: "bg-stone-100 text-stone-600", icon: FileText },
  { value: "pending_review", label: "Pending Review", color: "bg-amber-100 text-amber-700", icon: Clock },
  { value: "approved", label: "Approved", color: "bg-emerald-100 text-emerald-700", icon: CheckCircle2 },
  { value: "rejected", label: "Rejected", color: "bg-red-100 text-red-700", icon: XCircle },
  { value: "revision_requested", label: "Revision Requested", color: "bg-orange-100 text-orange-700", icon: AlertCircle },
];

export function ApprovalBadge({ status }) {
  const s = APPROVAL_STATUSES.find(st => st.value === status) || APPROVAL_STATUSES[0];
  const Icon = s.icon;
  return (
    <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium flex items-center gap-1 ${s.color}`}>
      <Icon className="w-3 h-3" />
      {s.label}
    </span>
  );
}

export function StatusBadge({ status, statuses }) {
  const s = statuses.find(st => st.value === status);
  if (!s) return null;
  return <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${s.color}`}>{s.label}</span>;
}

export function AssetCard({ asset, onSelect, onEdit, onDelete, statuses }) {
  return (
    <Card 
      data-testid={`asset-card-${asset.id}`} 
      className="bg-white border border-stone-200/60 rounded-xl overflow-hidden hover:shadow-md transition-shadow cursor-pointer group" 
      onClick={() => onSelect(asset)}
    >
      {asset.file_type === "image" && (
        <div className="aspect-video bg-stone-100 overflow-hidden">
          <img src={asset.file_url} alt={asset.name} className="w-full h-full object-cover" />
        </div>
      )}
      {asset.file_type === "video" && (
        <div className="aspect-video bg-stone-900 overflow-hidden">
          <video src={asset.file_url} className="w-full h-full object-cover" />
        </div>
      )}
      {asset.file_type === "document" && (
        <div className="aspect-video bg-stone-50 flex items-center justify-center">
          <FileText className="w-12 h-12 text-stone-300" />
        </div>
      )}
      <CardContent className="p-3">
        <div className="flex items-start justify-between gap-2 mb-1.5">
          <h4 className="text-sm font-semibold text-stone-900 truncate">{asset.name}</h4>
          <div className="flex items-center gap-0.5 flex-shrink-0">
            {asset.version > 1 && <span className="text-[10px] px-1.5 py-0.5 bg-blue-50 text-blue-600 rounded-full">v{asset.version}</span>}
            {asset.is_finalized && <Star className="w-4 h-4 text-amber-500" fill="currentColor" />}
          </div>
        </div>
        
        {/* Approval Status Badge */}
        {asset.approval_status && (
          <div className="mb-2">
            <ApprovalBadge status={asset.approval_status} />
          </div>
        )}
        
        {asset.status && statuses.length > 0 && (
          <div className="mb-2"><StatusBadge status={asset.status} statuses={statuses} /></div>
        )}
        {asset.tags?.length > 0 && (
          <div className="flex flex-wrap gap-1 mb-2">
            {asset.tags.slice(0, 3).map(t => <span key={t} className="text-[10px] px-1.5 py-0.5 bg-stone-100 text-stone-500 rounded-full">{t}</span>)}
          </div>
        )}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3 text-xs text-stone-400">
            <span className="flex items-center gap-1"><ThumbsUp className="w-3 h-3" /> {asset.thumbs_up}</span>
            <span className="flex items-center gap-1"><ThumbsDown className="w-3 h-3" /> {asset.thumbs_down}</span>
            <span className="flex items-center gap-1"><MessageSquare className="w-3 h-3" /> {asset.feedback?.length || 0}</span>
          </div>
          <div className="flex gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
            <button onClick={(e) => { e.stopPropagation(); onEdit(asset); }} className="p-1 text-stone-400 hover:text-stone-700" data-testid={`edit-asset-${asset.id}`}>
              <Edit2 className="w-3.5 h-3.5" />
            </button>
            <button onClick={(e) => { e.stopPropagation(); onDelete(asset.id); }} className="p-1 text-stone-400 hover:text-red-500" data-testid={`delete-asset-${asset.id}`}>
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
